import React from 'react'
import { useMutation } from '@tanstack/react-query'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import { TicketShare } from './Components/TicketShare'
import { BetSlipShare } from './Components/BetSlipShare'
import { QuickbetShare } from './Components/QuickbetShare'
import { isLoadBetSupportedWithShare } from '../../helpers'
import { PreviewDisplayFrame } from './Components/Common/PreviewDisplayFrame'
import { useCreateImageForBrowser } from './hooks/useCreateImageForBrowser'
import { handleShareBetClick } from './helpers/handleShareBetClick'
import { resetTemporaryWidthOfPreviewElement, setTemporaryWidthOfPreviewElement } from './helpers'

export const ShareWithExternal: React.FC<{ data: ShareBetData }> = ({ data }) => {
  const previewImageElRef = React.useRef<HTMLDivElement>(null)
  const [isCostShared, setShouldHideCost] = React.useState(false)
  const [isBusy, setIsBusy] = React.useState(false)

  const shouldEnableLinkShare = shouldAllowLinkSharing(data) && isLoadBetSupportedWithShare(data)

  const browserFileBlob = useCreateImageForBrowser({
    shouldHideCost: !isCostShared,
    shouldCreateImage: !shouldEnableLinkShare,
    setIsBusy,
    previewImageElRef,
  })

  const { mutate, isError, isPending } = useMutation({
    mutationFn: async () => {
      const previewEl = previewImageElRef.current
      previewEl && setTemporaryWidthOfPreviewElement(previewEl)
      await handleShareBetClick({
        data,
        previewEl,
        browserFileBlob,
        isImageOnlyShare: !shouldEnableLinkShare,
        isCostShared,
      })
    },
    onSettled() {
      const previewEl = previewImageElRef.current
      previewEl && resetTemporaryWidthOfPreviewElement(previewEl)
    },
  })

  const shouldEnableCostToggle = determineShouldEnableCostToggle(data)
  const handleToggleShouldShowCost = () => setShouldHideCost(curr => !curr)

  return (
    <PreviewDisplayFrame
      isError={isError}
      isLoading={isPending || isBusy}
      shareImageElRef={previewImageElRef}
      handleShareClick={mutate}
      handleToggleShouldShowCost={shouldEnableCostToggle ? handleToggleShouldShowCost : undefined}
      shouldShowCost={isCostShared}
    >
      <>
        {data.contextType === 'ticket' && (
          <TicketShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'betslip' && (
          <BetSlipShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'quickbet' && (
          <QuickbetShare share={data.share} isCostShared={isCostShared} />
        )}
      </>
    </PreviewDisplayFrame>
  )
}

// =============
// Local Helpers
// =============

function shouldAllowLinkSharing(data: ShareBetData): boolean {
  if (data.contextType === 'betslip') return true
  if (data.contextType === 'quickbet') return true
  return data.contextType === 'ticket' && data.share.activeTab === 'pending'
}

function determineShouldEnableCostToggle(data: ShareBetData): boolean {
  if (data.contextType === 'betslip') return data.share.isBetPlaced

  if (data.contextType === 'quickbet') {
    const { win, place } = data.share.items[0].investment
    const hasValidInvestment = !!(win.value || place.value)
    return hasValidInvestment
  }

  return true
}
